import { useCallback, useState } from "react";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import {
  CV_BAR_ICON_PATHS,
  CV_BAR_ICON_COLOR,
} from "../icons/CvBottomBarIcons";
import { buildMyTabbioRoute } from "../../lib/cv-target-route";
import { useTheme } from "../../hooks/useTheme";
import { CvAnalyticsSheet } from "../analytics/CvAnalyticsSheet";
import { CvSetupSheet } from "./CvSetupSheet";
import { CvHealthSheet } from "./CvHealthSheet";
import { useCvHealthDock } from "./use-cv-health-dock";
import { ActionDock } from "../common/action-dock/ActionDock";

import {
  buildCvBottomBarEditorRoute,
  getCvBottomBarTarget,
} from "./cv-bottom-bar-routing";

interface CVBottomBarProps {
  target?: string | null;
  /**
   * fixed: centered in the viewport.
   * content: centered inside the nearest positioned content pane.
   */
  placement?: "fixed" | "content";
  /**
   * Tailored CV detail pages reuse this visual dock but need their edit/theme
   * routes to stay inside `/cv/tailored/:id`.
   */
  mode?: "main" | "tailored";
  tailoredId?: string | null;
  onCustomizePress?: () => void;
}

function OutlineGlyph({
  paths,
  color,
}: {
  paths: readonly string[];
  color: string;
}) {
  return (
    <svg width={18} height={18} viewBox="0 0 18 18" fill="none">
      {paths.map((d) => (
        <path
          key={d}
          d={d}
          stroke={color}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
}

function tailoredIdFromLocation() {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(/\/cv\/tailored\/([^/]+)/);
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * The web CV dock carries no Enhance action at all — neither the prompt
 * composer nor the tailored page's in-place AI rewrite. On web the AI chat
 * column already sits beside the dock, so both are asked for there. Native has
 * no such column and keeps its own Enhance (composer on /cv, in-place rewrite
 * on the tailored detail screen's TailoredBottomBar).
 */
export function CVBottomBar({
  target,
  placement = "fixed",
  mode = "main",
  tailoredId,
  onCustomizePress,
}: CVBottomBarProps) {
  const router = useRouter();
  const { t } = useTranslation("seeker");
  const { theme, isDark } = useTheme();
  const [setupOpen, setSetupOpen] = useState(false);
  const [healthOpen, setHealthOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const openHealth = useCallback(() => setHealthOpen(true), []);
  // CV Health takes the ring slot when it can run; otherwise the ring stays.
  const { health, sheetSupported, completionPercent } =
    useCvHealthDock(openHealth);
  const tailoredRouteId =
    mode === "tailored"
      ? tailoredId?.trim() || tailoredIdFromLocation()
      : tailoredIdFromLocation();
  const effectiveTarget = getCvBottomBarTarget(target, tailoredRouteId);
  const buildEditorRoute = (path: string) =>
    buildCvBottomBarEditorRoute(path, effectiveTarget, tailoredRouteId);
  const iconColor = isDark ? theme.text : CV_BAR_ICON_COLOR;
  const safePercent = Math.max(0, Math.min(100, Math.round(completionPercent)));
  const setupRouteBuilder = tailoredRouteId ? buildEditorRoute : undefined;
  const openEdit = () =>
    router.push(buildEditorRoute("/(app)/cv/edit-sections"));
  const openCustomize = () => {
    if (onCustomizePress) {
      onCustomizePress();
      return;
    }
    router.push(buildEditorRoute("/(app)/cv/customize"));
  };

  return (
    <>
      <ActionDock
        placement={placement}
        ring={
          health
            ? undefined
            : {
                completionPercent,
                label: t("cv.setup.label"),
                a11yLabel: t("cv.setup.progress", { percent: safePercent }),
                a11yHint: t("cv.setup.openHint"),
                onPress: () => setSetupOpen(true),
              }
        }
        health={health ?? undefined}
        items={[
          {
            id: "cvs",
            icon: (
              <OutlineGlyph paths={CV_BAR_ICON_PATHS.cvs} color={iconColor} />
            ),
            label: t("cv.bottomBar.cvs"),
            onPress: () => router.navigate(buildMyTabbioRoute()),
          },
          {
            id: "edit",
            icon: (
              <OutlineGlyph paths={CV_BAR_ICON_PATHS.edit} color={iconColor} />
            ),
            label: t("cv.bottomBar.edit"),
            onPress: openEdit,
          },
          {
            id: "theme",
            icon: (
              <OutlineGlyph paths={CV_BAR_ICON_PATHS.theme} color={iconColor} />
            ),
            label: t("cv.bottomBar.theme"),
            onPress: openCustomize,
          },
          {
            id: "analytics",
            icon: (
              <OutlineGlyph
                paths={CV_BAR_ICON_PATHS.analytics}
                color={iconColor}
              />
            ),
            label: t("cv.bottomBar.analytics"),
            onPress: () => setAnalyticsOpen(true),
          },
        ]}

      />
      <CvSetupSheet
        visible={setupOpen}
        onClose={() => setSetupOpen(false)}
        target={effectiveTarget}
        buildEditorRoute={setupRouteBuilder}
      />
      {sheetSupported ? (
        <CvHealthSheet
          visible={healthOpen}
          onClose={() => setHealthOpen(false)}
        />
      ) : null}
      <CvAnalyticsSheet
        visible={analyticsOpen}
        onClose={() => setAnalyticsOpen(false)}
        target={effectiveTarget}
      />
    </>
  );
}
